import React, { createContext, useContext, useState, ReactNode } from "react";

type NotificationType = "success" | "error" | "warning";

interface GlobalNotificationContextType {
  message: React.ReactNode;
  showIcon: boolean;
  type: NotificationType;
  setNotification: (
    msg: React.ReactNode,
    type?: NotificationType,
    noTimeout?: boolean,
    showIcon?: boolean
  ) => void;
  removeNotification: () => void;
}

const GlobalNotificationContext = createContext<
  GlobalNotificationContextType | undefined
>(undefined);

export const GlobalNotificationProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [message, setMessage] = useState<React.ReactNode>(null);
  const [type, setType] = useState<NotificationType>("success");
  const [showIcon, setShowIcon] = useState<boolean>(true);

  const removeNotification = () => {
    setMessage(null);
  };

  const setNotification = (
    msg: React.ReactNode,
    type: NotificationType = "success",
    noTimeout: boolean = false,
    showIcon: boolean = false
  ) => {
    setMessage(msg);
    setType(type);
    setShowIcon(showIcon ?? true);

    if (!noTimeout && msg !== null) {
      setTimeout(() => {
        setMessage(null);
      }, 10000);
    }

    if (msg) {
      setTimeout(() => {
        const scrollingElement =
          document.scrollingElement ||
          document.documentElement ||
          document.body;

        scrollingElement.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }, 50);
    }
  };

  return (
    <GlobalNotificationContext.Provider
      value={{ message, showIcon, type, setNotification, removeNotification }}
    >
      {children}
    </GlobalNotificationContext.Provider>
  );
};

export const useGlobalNotification = () => {
  const context = useContext(GlobalNotificationContext);
  if (!context) {
    throw new Error(
      "useGlobalNotification must be used within a GlobalNotificationProvider"
    );
  }
  return context;
};
